<script setup lang="ts">
import { computed } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';
import TaskStatusBadge from '@Components/TaskStatusBadge.vue';
import { useTaskNavigation } from '@Use/useTaskNavigation';
import { formatDate, formatRelativeDays, formatShortDate } from '@Src/shared/utils/formatDate';
import { resolveEffectiveType, isMatchTask } from '@Src/shared/utils/resolveEffectiveType';
import { NEW_TASK_LABELS, IN_PROGRESS_LABELS, resolveTaskCardTitle } from '@Src/components/taskCardLabels';

const props = defineProps<{
    task: TaskItem;
}>();

const { navigateToTask, isNavigating, navigationError, isTaskNavigable } = useTaskNavigation();

const variant = computed(() => {
    if (props.task.status === 'completed') return 'completed';
    if (props.task.status === 'in_progress') return 'in_progress';
    return 'new';
});

const isActionable = computed(() =>
    (variant.value === 'new' || variant.value === 'in_progress')
    && !isRepaymentPending.value
    && isTaskNavigable(props.task));
const isCompleted = computed(() => variant.value === 'completed');
const isLocked = computed(() => props.task.isLocked === true);

// A completed payment_request that offered digital payment (digital_payment_accepted)
// but has no transfer_id — i.e. the buyer received wire instructions rather than
// paying digitally. When digital payment wasn't offered the task resolves to `send`
// (see resolveEffectiveType), so this flag is scoped to the digital-accepted case.
const isWireInstructionsCompleted = computed(() =>
    props.task.taskType === 'payment_request' &&
    props.task.taskData?.digital_payment_accepted === true &&
    isCompleted.value &&
    !props.task.taskData?.transfer_id);

// DS-2893: gating helpers for the match-failed state.
const isMatch = computed(() => isMatchTask(props.task));
const isMatchFailed = computed(() =>
    isMatch.value &&
    (props.task.matchOutcome === 'failed' || props.task.matchOutcome === 'unable_to_verify'));

const isRepayment = computed(() => effectiveType.value === 'payment_repayment');

const initiatedAt = computed(() => {
    const v = props.task.taskData?.initiated_at;
    return typeof v === 'string' ? v : null;
});

// PAY-3517: `status` alone can't distinguish "buyer submitted the repayment"
// (SubmitRepaymentPipeline.AdvanceTask stamps status=in_progress + initiated_at
// together) from "buyer merely opened the priming sheet" (useTaskPriming.openSheet
// flips pending -> in_progress on view, before any submission, with no initiated_at).
// Gating on initiatedAt too keeps the CTA available until a real submission exists.
const isRepaymentPending = computed(() =>
    isRepayment.value && props.task.status === 'in_progress' && !!initiatedAt.value);

// DS-2702/DS-2893: locked CTA copy varies by the locker's match outcome.
const lockedCtaLabel = computed(() => {
    const lockerOutcome = props.task.lockerMatchOutcome;
    if (lockerOutcome === 'high_risk') {
        return 'Contact your agent';
    }
    if (lockerOutcome === 'failed' || lockerOutcome === 'unable_to_verify') {
        return 'Retry ID Verification first';
    }
    return 'Complete ID Verification First';
});

const dateLabel = computed(() => {
    // DS-2893: hide the date on a failed match; subtitleLabel carries the hint.
    if (isMatchFailed.value) return null;
    // Completed wire-instructions request: parallel to the "Completed on" copy.
    if (isWireInstructionsCompleted.value && props.task.completedDate) {
        return `Wire instructions viewed on ${formatDate(props.task.completedDate)}`;
    }
    if (isRepaymentPending.value) {
        const formatted = initiatedAt.value ? formatShortDate(initiatedAt.value) : '';
        return formatted ? `You initiated a payment on ${formatted}` : null;
    }

    if (isCompleted.value && isRepayment.value && props.task.completedDate) {
        const formatted = formatShortDate(props.task.completedDate);
        if (formatted) return `Success! Your payment was completed on ${formatted}`;
    }
    // DS-5050/PS-1022: "Completed on MM/DD/YYYY" per ticket copy.
    if (isCompleted.value && props.task.completedDate) {
        return `Completed on ${formatDate(props.task.completedDate)}`;
    }
    // DS-5050/PS-1022: relative days since createdDate ("Requested 2 days ago"),
    // no threshold per Product; falls back to dueDate.
    if (!isCompleted.value && props.task.createdDate) {
        return `Requested ${formatRelativeDays(props.task.createdDate)}`;
    }
    if (props.task.dueDate) {
        return `Due ${formatDate(props.task.dueDate)}`;
    }
    return null;
});

// DS-2893: hint shown instead of a date on a negative match outcome.
const subtitleLabel = computed(() => {
    if (props.task.matchOutcome === 'high_risk') return 'Contact your closing agent to continue.';
    if (isMatchFailed.value) return 'Please try again to continue.';
    if (isRepaymentPending.value) return 'No action required at the moment! Payments usually take 2 business days to complete.';
    return null;
});

const subtitleTestId = computed(() =>
    isRepaymentPending.value ? 'repayment-body' : 'match-failed-subtitle');

// Shared with priming/completion so alias resolution never drifts.
const effectiveType = computed(() => resolveEffectiveType(props.task) ?? '');

// Completed payment-request tasks (generic / EMD / C2C) don't offer a CTA, except
// the wire-instructions case (isWireInstructionsCompleted) which re-opens the instructions.
const isPaymentRequestType = computed(() =>
    ['payment_request', 'payment_request_emd', 'payment_request_c2c', 'payment_request_other'].includes(effectiveType.value));

// Completed payment requests only offer a CTA in the wire-instructions case;
// other completed payment requests (e.g. a paid digital transfer) suppress it.
const showsCompletedCta = computed(() =>
    isCompleted.value
    && isTaskNavigable(props.task)
    && (!isPaymentRequestType.value || isWireInstructionsCompleted.value));

// Whether any CTA branch will render; when false the action column is omitted
// so the card reflows without a reserved gap (e.g. a completed payment request).
const hasAction = computed(() =>
    isLocked.value ||
    isMatchFailed.value ||
    isActionable.value ||
    showsCompletedCta.value);

const buttonLabel = computed(() => {
    // DS-2893: failed match always offers a retry path.
    if (isMatchFailed.value) return 'Retry verification';
    if (isCompleted.value) return 'View details';
    const type = effectiveType.value;
    if (props.task.status === 'in_progress' && IN_PROGRESS_LABELS[type]) {
        return IN_PROGRESS_LABELS[type];
    }
    return NEW_TASK_LABELS[type] ?? 'View details';
});

// `||` not `??`: title is `string | null`, and an empty string must fall through to the
// type-based title rather than render a blank heading.
const taskTitle = computed(() =>
    props.task.title || resolveTaskCardTitle(effectiveType.value, props.task.taskData));

const handleClick = () => {
    navigateToTask(props.task);
};
</script>

<template>
    <article
        class="task-card"
        :class="[
            `task-card--${variant}`,
            { 'task-card--locked': isLocked },
            { 'task-card--match-failed': isMatchFailed },
        ]"
        data-test="task-card"
    >
        <div class="task-card__content">
            <div class="task-card__title-row">
                <template v-if="isMatchFailed">
                    <!-- DS-2893: error icon + headline replace the status badge -->
                    <span class="task-card__match-failed-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                    </span>
                    <h3 class="task-card__title task-card__title--error">
                        Identity verification was not successful
                    </h3>
                </template>
                <template v-else>
                    <h3 class="task-card__title">{{ taskTitle }}</h3>
                    <TaskStatusBadge v-if="!isLocked" :status="task.status ?? 'pending'" />
                </template>
            </div>

            <div v-if="dateLabel" class="task-card__meta">
                <span class="task-card__meta-icon">
                    <svg v-if="isCompleted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M5 13l4 4L19 7" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 3" />
                    </svg>
                </span>
                <span class="task-card__meta-text" data-test="task-card-meta">{{ dateLabel }}</span>
            </div>

            <!-- DS-2893: hint shown instead of a date when match failed -->
            <p v-if="subtitleLabel" class="task-card__subtitle" :data-test="subtitleTestId">
                {{ subtitleLabel }}
            </p>
        </div>

        <div v-if="hasAction" class="task-card__action">
            <app-button
                v-if="isLocked"
                expanded
                size="large"
                type="primary"
                :is-disabled="true"
                data-test="task-card-cta-locked"
            >
                {{ lockedCtaLabel }}
            </app-button>
            <!-- DS-2893: match-failed CTA is always actionable (retry flow) -->
            <app-button
                v-else-if="isMatchFailed"
                expanded
                size="large"
                type="primary"
                :is-loading="isNavigating"
                data-test="task-card-cta"
                @onClick="handleClick"
            >
                {{ buttonLabel }}
            </app-button>
            <app-button
                v-else-if="isActionable"
                expanded
                size="large"
                type="primary"
                :is-loading="isNavigating"
                data-test="task-card-cta"
                @onClick="handleClick"
            >
                {{ buttonLabel }}
            </app-button>
            <app-button
                v-else-if="showsCompletedCta"
                expanded
                size="large"
                type="outline"
                :is-loading="isNavigating"
                @onClick="handleClick"
            >
                {{ buttonLabel }}
            </app-button>
        </div>
        <p v-if="navigationError" class="task-card__error">{{ navigationError }}</p>
    </article>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.task-card {
    // Mobile (default): vertical stack — title+badge row, meta row, full-width CTA.
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    border-radius: $portal-card-radius;
    padding: $portal-card-padding;
    background: $white;
    box-shadow: $portal-card-shadow;

    // Desktop: horizontal row; border instead of shadow.
    @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
        box-shadow: none;
        border: 1px solid $portal-border;
    }

    &__content {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;

        @media (min-width: 768px) {
            flex: 1;
        }
    }

    &__title-row {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        // Mobile: badge pushed to the right edge; desktop: inline after title.
        justify-content: space-between;

        @media (min-width: 768px) {
            justify-content: flex-start;
        }
    }

    &__title {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        color: $black-high;
        margin: 0;
        line-height: 1.5;

        // DS-2893: error headline uses the existing $red token.
        &--error {
            color: $red;
        }
    }

    &__meta {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__meta-icon {
        width: $portal-status-circle;
        height: $portal-status-circle;
        border-radius: 50%;
        // DS-5050: brand-aware light blue per design node 1642:25568.
        background: var(--ch-primary-lightest, #DEEFF9);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: var(--ch-primary, #156FBE);

        svg {
            width: $portal-status-icon;
            height: $portal-status-icon;
        }
    }

    &--completed &__meta-icon {
        background: #CBF5DC;
        color: #0B351C;
    }

    &__meta-text {
        font-size: $text-portal-small;
        font-weight: 700;
        color: $black-med;
    }

    // DS-2893: warning icon for the match-failed title row.
    &__match-failed-icon {
        width: $portal-status-circle;
        height: $portal-status-circle;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: $red;

        svg {
            width: $portal-status-icon;
            height: $portal-status-icon;
        }
    }

    // DS-2893: hint copy for a negative match outcome.
    &__subtitle {
        font-size: $text-portal-small;
        color: $black-med;
        margin: 0;
    }

    &__action {
        flex-shrink: 0;
        // Mobile: full-width CTA. Desktop: fixed 160px for uniform widths.
        width: 100%;

        @media (min-width: 768px) {
            width: 160px;
        }

        // uilib owns button chrome; font-size overridden to 14px per design.
        :deep(button) {
            font-size: 14px;
        }
    }

    // DS-2702: locked task — content fades to half opacity; CTA gets its own
    // solid-grey disabled treatment instead of a faded primary.
    &--locked {
        pointer-events: none;

        .task-card__content {
            opacity: 0.5;
        }

        .task-card__action {
            // Locked copy is longer than normal CTAs; let width grow on desktop.
            @media (min-width: 768px) {
                width: auto;
            }

            :deep(button[disabled]) {
                background: $disabled-input-bgcolor;
                color: $disabled-color;
            }
        }
    }

    // DS-2893: match-failed is actionable, not muted. Assumes Match is never
    // also `--locked`; revisit if that invariant changes.
    &--match-failed {
        pointer-events: auto;

        .task-card__content {
            opacity: 1;
        }
    }

    &__error {
        font-size: $text-portal-small;
        color: $red;
        margin: 4px 0 0;
        padding: 0 16px;
    }
}
</style>
